// frontend/src/contexts/DocumentsContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import api, { Document } from "../services/api";
import { useAuth } from "./AuthContext";

interface DocumentsContextType {
  documents: Document[];
  loading: boolean;
  error: string | null;
  refreshDocuments: () => Promise<void>;
  sortDirection: "DESC" | "ASC";
  setSortDirection: (
    direction: "DESC" | "ASC" | ((prev: "DESC" | "ASC") => "DESC" | "ASC")
  ) => void;
  isAdminView: boolean;
  setIsAdminView: (isAdmin: boolean) => void;
  showHidden: boolean;
  setShowHidden: (show: boolean) => void;
  toggleDocumentVisibility: (
    docId: number,
    currentVisibility: boolean
  ) => Promise<void>;
  setDocuments: (docs: Document[]) => void;
  setNeedsUpdates: (needs: boolean) => void;
}

const DocumentsContext = createContext<DocumentsContextType | null>(null);

export const DocumentsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"DESC" | "ASC">("DESC");
  const [isAdminView, setIsAdminView] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [needsUpdates, setNeedsUpdates] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchDocuments = useCallback(async () => {
    if (!user) return;

    try {
      setError(null);
      const response = await api.get("/documents", {
        params: {
          sort_direction: "DESC",
          admin_view: isAdminView,
        },
      });

      if (response.data.success) {
        setDocuments(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Error loading documents");
    }
  }, [user, isAdminView]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (needsUpdates) {
      fetchDocuments();

      intervalId = setInterval(() => {
        fetchDocuments();
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [needsUpdates, fetchDocuments]);

  useEffect(() => {
    setLoading(true);
    fetchDocuments().finally(() => setLoading(false));
  }, [fetchDocuments]);

  const toggleDocumentVisibility = useCallback(
    async (docId: number, currentVisibility: boolean) => {
      const newVisibility = !currentVisibility;
      
      try {
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === docId ? { ...doc, is_visible: newVisibility } : doc
          )
        );

        const response = await api.patch(`/documents/${docId}`);

        if (!response.data.success) {
          setDocuments((prev) =>
            prev.map((doc) =>
              doc.id === docId ? { ...doc, is_visible: currentVisibility } : doc
            )
          );
        }
      } catch (err) {
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === docId ? { ...doc, is_visible: currentVisibility } : doc
          )
        );
        throw err;
      }
    },
    []
  );

  const sortDocuments = useCallback(
    (docs: Document[]) => {
      return [...docs].sort((a, b) => {
        const dateA = new Date(a.exam_date).getTime();
        const dateB = new Date(b.exam_date).getTime();
        if (dateA !== dateB) {
          return sortDirection === "DESC" ? dateB - dateA : dateA - dateB;
        }
        return a.id - b.id;
      });
    },
    [sortDirection]
  );

  const sortedDocuments = useMemo(() => {
    const filteredDocs = isAdminView
      ? documents
      : documents.filter((doc) => doc.is_visible);

    return sortDocuments(filteredDocs);
  }, [documents, isAdminView, sortDocuments]);

  const handleSetSortDirection = useCallback(
    (
      direction: "DESC" | "ASC" | ((prev: "DESC" | "ASC") => "DESC" | "ASC")
    ) => {
      setSortDirection(
        typeof direction === "function" ? direction(sortDirection) : direction
      );
    },
    [sortDirection]
  );

  const contextValue = useMemo(
    () => ({
      documents: sortedDocuments,
      loading,
      error,
      refreshDocuments: fetchDocuments,
      sortDirection,
      setSortDirection: handleSetSortDirection,
      isAdminView,
      setIsAdminView,
      showHidden,
      setShowHidden,
      toggleDocumentVisibility,
      setDocuments,
      setNeedsUpdates,
    }),
    [
      sortedDocuments,
      loading,
      error,
      fetchDocuments,
      sortDirection,
      handleSetSortDirection,
      isAdminView,
      showHidden,
      toggleDocumentVisibility,
      setNeedsUpdates,
    ]
  );

  return (
    <DocumentsContext.Provider value={contextValue}>
      {children}
    </DocumentsContext.Provider>
  );
};

export const useDocuments = () => {
  const context = useContext(DocumentsContext);
  if (!context) {
    throw new Error("useDocuments must be used within a DocumentsProvider");
  }
  return context;
};
